import { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getBookingDetails } from '@/utils/endUserAppApi';
import { useTranslation } from 'react-i18next';

interface PaymentFormProps {
  amount: number;
  onSuccess: () => void;
  onError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (state: boolean) => void;
  bookingId: string;
  returnUrl?: string;
}

export function PaymentForm({ 
  amount, 
  onSuccess, 
  onError,
  isProcessing,
  setIsProcessing,
  bookingId,
  returnUrl
}: PaymentFormProps) {
  const { t } = useTranslation('business', { keyPrefix: 'payment' });
  const stripe = useStripe();
  const elements = useElements();
  const [paymentStatus, setPaymentStatus] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setPaymentStatus(t('processingPayment'));

    try {
      // Confirm the payment
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl || `${window.location.origin}/confirmation/${bookingId}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        onError(error.message ?? 'Payment failed');
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        setPaymentStatus(t('paymentSuccessful'));
        
        // Poll for booking confirmation
        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = 1000; // 1 second

        while (attempts < maxAttempts) {
          const bookingDetails = await getBookingDetails(bookingId);
          
          if (bookingDetails.status === 'confirmed') {
            onSuccess();
            return;
          }

          await new Promise(resolve => setTimeout(resolve, pollInterval));
          attempts++;
        }

        // If we get here, the webhook might have failed
        setPaymentStatus(t('paymentReceivedNotConfirmed'));
        onError('Payment processed but booking status not updated');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      onError(error.response?.data?.error || 'An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md mx-auto space-y-6">
      <div className="p-4 border rounded-lg bg-white shadow-sm">
        <PaymentElement />
      </div>
      
      {paymentStatus && (
        <div className="text-sm text-center text-muted-foreground">
          {paymentStatus}
        </div>
      )}
      
      <Button
        type="submit"
        className={cn(
          "w-full",
          isProcessing && "opacity-50 cursor-not-allowed"
        )}
        disabled={isProcessing || !stripe || !elements}
      >
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2" />
            {t('processing')}
          </div>
        ) : (
          <div className="flex items-center justify-center">
            <CreditCard className="mr-2 h-5 w-5" />
            {t('payAmount', { amount: amount.toFixed(2) })}
          </div>
        )}
      </Button>
    </form>
  );
}